// --------------------------------------------------------
// Meus Pacientes Page (UC2025_15)
// --------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../..';
import { CompanyListContainer } from '../company/CompanyList.styled';
import { GeneralContainer, ListItemContainer, Table, TableData, Text } from '../../components/UI/generalLayout/Layout.styled';
import { CircularProgress, InputAdornment, OutlinedInput, TablePagination, Box, Typography } from '@mui/material';
import { MobileContainer } from '../medicalRecord/MedicalRecordList.styled';
import Search from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import ScreenHeader from '../../components/UI/screenHeader/ScreenHeader';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import Colors from '../../constants/Colors';
import { ItemInfoContainer, ViewEditContainer } from '../../components/companyListItem/CompanyListItemMobile.styled';
import { useNavigate } from 'react-router-dom';
import { DivProntuario } from '../../components/cardPainelConsults/CardPainelConsults.styled';
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Paths } from '../../enums/Paths';
import moment from "moment";
import { PersonService } from '../../core/http/service/PersonService';
import { atualizaAcessoProntuario } from '../../store/actions/person';

function MeusPacientes(): JSX.Element {
    const { isMobile } = useResponsiveInfo();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { currentCompany } = useSelector((state: any) => state.login);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [patients, setPatients] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    // Debounce search query changes to prevent excessive API calls
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPage(0);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchPatients = useCallback(async () => {
        if (!currentCompany || !currentCompany.id) return;
        setIsLoading(true);
        const service = new PersonService();
        try {
            const sanitizedSearch = debouncedSearchQuery.replace(/[.-]/g, '');
            const response: any = await service.getMeusPacientes(
                currentCompany.id,
                page + 1,
                rowsPerPage,
                sanitizedSearch
            );
            if (response && response.status === 200 && response.data) {
                setPatients(response.data.data || []);
                setTotalRows(response.data.totalRows || 0);
            } else {
                setPatients([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
            setPatients([]);
            setTotalRows(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentCompany, page, rowsPerPage, debouncedSearchQuery]);

    useEffect(() => {
        fetchPatients();
    }, [fetchPatients]);

    const formatDateToBR = (dateString?: string) => {
        if (!dateString) return "";
        return moment(dateString).format("DD/MM/YYYY");
    };

    const formatCPF = (value?: string) => {
        if (!value) return "";
        const clean = value.replace(/\D/g, '');
        if (clean.length !== 11) return value;
        return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    };

    const redirecionarProntuarioPaciente = async (pacienteId: string) => {
        const service = new PersonService();
        try {
            const result: any = await service.getPessoaById(pacienteId);
            if (result && result.status === 200 && result.data) {
                const pessoaAtual = result.data.data;
                dispatch(atualizaAcessoProntuario(true));
                navigate(Paths.Prontuario, { state: { pessoa: pessoaAtual } });
            }
        } catch (error) {
            console.error("Error loading patient data for medical record redirection:", error);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <ContainerWithSidebar>
            <GeneralContainer>
                <ScreenHeader
                    screenTitle="Meus Pacientes"
                    onRightButtonClick={() => { }}
                    hasRightButton={false}
                    rightButtonText=""
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', marginTop: 16, marginBottom: 24 }}>
                    <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OutlinedInput
                            style={{ borderRadius: "10px", width: isMobile ? "100%" : "450px", height: "45px", backgroundColor: "#fff" }}
                            id="outlined-adornment-search"
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            }
                            endAdornment={
                                isLoading ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} color="primary" />
                                    </InputAdornment>
                                ) : null
                            }
                            placeholder="Busca por Nome ou CPF"
                            value={searchQuery}
                            onChange={(event: any) => {
                                setSearchQuery(event.target.value);
                            }}
                        />
                    </div>
                </div>

                {!isMobile && patients && patients.length > 0 && (
                    <Table style={{ width: "100%", marginTop: 10, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TableData width="38%"><Text bold style={{ color: Colors.GRAY700 }}>Nome</Text></TableData>
                                <TableData width="23%"><Text bold style={{ color: Colors.GRAY700 }}>Nascimento</Text></TableData>
                                <TableData width="20%"><Text bold style={{ color: Colors.GRAY700 }}>CPF</Text></TableData>
                                <TableData width="19%"><Text bold></Text></TableData>
                            </tr>
                        </thead>
                    </Table>
                )}

                {isLoading && patients.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, width: '100%' }}>
                        <CircularProgress />
                    </div>
                ) : patients && patients.length > 0 ? (
                    <CompanyListContainer style={{ marginBottom: isMobile ? 30 : 10, marginTop: 10 }}>
                        {patients.map((p: any) => (
                            <ListItemContainer key={p.id}>
                                {!isMobile ? (
                                    <Table>
                                        <tbody>
                                            <tr>
                                                <TableData width="38%">
                                                    <Text style={{ fontWeight: 600, color: "#1E293B" }}>{p.nome}</Text>
                                                </TableData>
                                                <TableData width="23%">
                                                    <Text style={{ color: "#475569" }}>{formatDateToBR(p.dataNascimento)}</Text>
                                                </TableData>
                                                <TableData width="20%">
                                                    <Text style={{ color: "#475569" }}>{formatCPF(p.cpf)}</Text>
                                                </TableData>
                                                <TableData width="19%">
                                                    <DivProntuario
                                                        onClick={() => redirecionarProntuarioPaciente(p.id)}
                                                    >
                                                        <span>PRONTUÁRIO</span>
                                                        <ArrowForwardIosIcon style={{ fontSize: 13 }} />
                                                    </DivProntuario>
                                                </TableData>
                                            </tr>
                                        </tbody>
                                    </Table>
                                ) : (
                                    <MobileContainer key={p.id}>
                                        <ItemInfoContainer style={{ textAlign: 'start' }}>
                                            <Text bold color={Colors.GRAY900}>{p.nome}</Text>
                                            <Text color="#8D8D8D" style={{ fontSize: 14, marginTop: -6 }}>
                                                {formatDateToBR(p.dataNascimento)}
                                            </Text>
                                            <Text>{formatCPF(p.cpf)}</Text>
                                            <ViewEditContainer style={{ width: 200, marginTop: 8 }}>
                                                <DivProntuario
                                                    onClick={() => redirecionarProntuarioPaciente(p.id)}
                                                >
                                                    <span>PRONTUÁRIO</span>
                                                    <ArrowForwardIosIcon style={{ fontSize: 13 }} />
                                                </DivProntuario>
                                            </ViewEditContainer>
                                        </ItemInfoContainer>
                                    </MobileContainer>
                                )}
                            </ListItemContainer>
                        ))}
                    </CompanyListContainer>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "48px 24px",
                            marginTop: "32px",
                            backgroundColor: "#F8FAFC",
                            border: "1px dashed #CBD5E1",
                            borderRadius: "16px",
                            textAlign: "center",
                            gap: "12px",
                        }}
                    >
                        <PersonOffIcon sx={{ fontSize: 48, color: Colors.SPM600, opacity: 0.85 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B" }}>
                            Nenhum paciente encontrado
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748B", maxWidth: 480, lineHeight: 1.5 }}>
                            {searchQuery.trim().length > 0
                                ? `Não encontramos nenhum paciente para o termo "${searchQuery}". Verifique se o nome ou CPF foi digitado corretamente.`
                                : "Nenhum paciente cadastrado foi localizado para esta unidade."}
                        </Typography>
                    </Box>
                )}

                {patients && patients.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalRows}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Linhas por página"
                    />
                )}
            </GeneralContainer>
        </ContainerWithSidebar>
    );
}

export default MeusPacientes;
